'use client';
import { Highlight } from '@curology/ui-components-web/react';
import { Button } from '@curology/ui-components-web/react/Button';
import { Typography } from '@curology/ui-components-web/react/Typography';
import React, { useEffect, useMemo, useState } from 'react';

import { useDataProvider } from '@/components/flow/components/DataProvider/useDataProvider';
import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { DATA_KEYS } from '@/consts/dataKeys';
import { GuestService } from '@/services/guestService/client';

import BirthdateInput from './components/birthdateInput';
import StateInput from './components/stateInput';
import TermsAndConditions from './components/termsAndConditions';
import VipDisclaimer from './components/vipDisclaimer';
import { FORM_CONFIG, SUBMIT_BUTTON_DATA_TESTID } from './config';
import { useEligibilityForm } from './hooks/useEligibilityForm';
import { useOfferTracking } from './utils';
import { captureInviteToken } from './utils/captureInviteToken';
import { captureReferrer } from './utils/captureReferrer';
import { getSubmitButtonText } from './utils/helpers';
import { reportReferrerUtm } from './utils/reportReferrerUtm';
import { EligibilityFormSchema } from './utils/validation';

const EligibilityForm: React.FC<{
  btnNextText?: string;
  selectionText?: string;
  shouldShowVipDisclaimer?: boolean;
  withTosCheckbox?: boolean;
}> = ({
  btnNextText,
  selectionText,
  shouldShowVipDisclaimer = false,
  withTosCheckbox = true,
}) => {
  const eligibilityData = useDataProvider(DATA_KEYS.eligibilityData);
  const [form, setForm] = useState({
    state: eligibilityData.state || FORM_CONFIG.state.defaultValue,
    zip: eligibilityData.zip || '',
    birthdate: eligibilityData.birthdate || '',
    hasAcceptedTerms: false,
  });

  const { treatmentCategory, brand } = useFlowContext();

  const { submit, processing, errors } = useEligibilityForm({
    treatmentCategory: treatmentCategory || 'skin',
    brand: brand,
  });

  useOfferTracking();

  useEffect(() => {
    if (brand) {
      GuestService.runCookieMiddleware(brand).then(() => {
        reportReferrerUtm(brand);
        captureReferrer();
        captureInviteToken();
      });
    }
  }, [brand]);

  const valid = useMemo(() => {
    return EligibilityFormSchema.safeParse(form).success;
  }, [form]);

  const setData = (data: Partial<typeof form>) => {
    setForm((prev) => ({ ...prev, ...data }));
  };

  return (
    <form className="data-hj-suppress" onSubmit={submit}>
      {errors?.formErrors && errors.formErrors.length > 0 && (
        <Highlight
          state="critical"
          title={
            <Typography>There was an error with your submission.</Typography>
          }
        >
          {errors?.formErrors.map((error) => (
            <Typography key={error} size="meta">
              {error}
            </Typography>
          ))}
        </Highlight>
      )}
      <StateInput
        birthdate={form.birthdate}
        setState={(state) => setData({ state })}
        state={form.state}
        brand={brand}
        treatmentCategory={treatmentCategory}
      />

      {selectionText && (
        <Typography className="cur-pt-2 cur-text-highlight" size="meta">
          {selectionText}
        </Typography>
      )}
      <BirthdateInput
        birthdate={form.birthdate}
        setBirthdate={(birthdate) => setData({ birthdate })}
        state={form.state}
        brand={brand}
        treatmentCategory={treatmentCategory}
      />
      {shouldShowVipDisclaimer && <VipDisclaimer />}
      <TermsAndConditions
        brand={brand}
        hasAcceptedTerms={form.hasAcceptedTerms}
        setHasAcceptedTerms={(hasAcceptedTerms) =>
          setData({ hasAcceptedTerms })
        }
        withTosCheckbox={withTosCheckbox}
      />
      <Button
        type="submit"
        className="cur-mt-3 cur-w-full"
        data-testid={SUBMIT_BUTTON_DATA_TESTID}
        loading={processing}
        disabled={processing || !valid}
      >
        {getSubmitButtonText(btnNextText, withTosCheckbox)}
      </Button>
    </form>
  );
};

export default EligibilityForm;
